/**
 * CreditAwarePurchase — wrapper achat avec application automatique Crédit Mytenue
 *
 * v1.12.853 — Phase 5 (étendu)
 *
 * Avant chaque achat (sub / addon / wardrobe sub) :
 *   1. Lit la balance crédit active de l'user
 *   2. Si > 0 : propose application via AppDialog "Appliquer X € ?"
 *   3. Délègue à Stripe (web) ou IAP (mobile) avec useCreditAmountEur
 *
 * Conformité L.521-1 :
 *   - Aucun retrait monétaire ajouté
 *   - Sur mobile, prix IAP inchangé (Apple/Play rules) → conversion en BONUS
 *     post-achat via revenuecat-webhook (jours offerts ou unités gratuites)
 */

import { Platform, Alert } from 'react-native';
import { getCreditBalance } from './MytenueCredits';
import { TIERS } from './PremiumService';
import { ADDONS } from '../config/addonsConfig';

const isWeb = Platform.OS === 'web';

function getSubscriptionPriceEur(tier, period) {
  const t = TIERS[tier];
  if (!t) return 0;
  return period === 'yearly' ? Number(t.priceYearly || 0) : Number(t.priceMonthly || 0);
}

function getAddonPriceEur(addonId) {
  return Number(ADDONS[addonId]?.priceEur || 0);
}

/**
 * Propose à l'user d'appliquer ses crédits avant achat.
 * Retourne montant à appliquer (0 si décline) ou null si pas de balance.
 */
async function promptCreditApplication({ token, itemPriceEur, itemLabel, getValidToken, lang = 'fr' }) {
  if (itemPriceEur <= 0) return 0;
  try {
    const authToken = getValidToken ? await getValidToken() : token;
    if (!authToken) return 0;
    const balance = await getCreditBalance(authToken);
    const active = Number(balance?.active || 0);
    if (active <= 0) return 0;
    const applicable = Math.min(active, itemPriceEur);
    if (applicable <= 0) return 0;

    const isFR = lang === 'fr';
    return new Promise((resolve) => {
      const reste = Math.max(0, itemPriceEur - applicable).toFixed(2);
      Alert.alert(
        isFR ? '✨ Crédit Mytenue disponible' : '✨ Mytenue Credits available',
        isFR
          ? `Vous avez ${active.toFixed(2)} € de Crédit Mytenue.\n\n` +
            `Appliquer ${applicable.toFixed(2)} € sur "${itemLabel}" (${itemPriceEur.toFixed(2)} €) ?\n` +
            (Number(reste) > 0 ? `Reste à payer : ${reste} €` : `Achat couvert à 100% par vos crédits.`)
          : `You have ${active.toFixed(2)} € of Mytenue Credits.\n\n` +
            `Apply ${applicable.toFixed(2)} € on "${itemLabel}" (${itemPriceEur.toFixed(2)} €) ?\n` +
            (Number(reste) > 0 ? `Remaining to pay: ${reste} €` : `Fully covered by your credits.`),
        [
          { text: isFR ? 'Sans crédit' : 'Without credits', style: 'cancel', onPress: () => resolve(0) },
          { text: isFR ? `Appliquer ${applicable.toFixed(2)} €` : `Apply ${applicable.toFixed(2)} €`,
            onPress: () => resolve(applicable) },
        ],
        { cancelable: true, onDismiss: () => resolve(0) }
      );
    });
  } catch (e) {
    console.warn('[CreditAwarePurchase] prompt failed:', e?.message);
    return 0;
  }
}

/**
 * Subscription (Stripe web OU IAP mobile)
 * @param {object} ctx { tier, period, user, getValidToken, lang }
 */
export async function purchaseSubscriptionWithCredits({ tier, period, user, getValidToken, lang = 'fr' }) {
  const priceEur = getSubscriptionPriceEur(tier, period);
  const label = `${(TIERS[tier]?.label || tier)} ${period === 'yearly' ? 'annuel' : 'mensuel'}`;
  const token = user?.accessToken;

  if (isWeb) {
    const useCreditAmountEur = await promptCreditApplication({
      token, getValidToken, itemPriceEur: priceEur, itemLabel: label, lang,
    });
    const { startCheckout } = require('./StripeService');
    return startCheckout(tier, period, { useCreditAmountEur });
  } else {
    // Mobile : Apple/Google interdisent modification prix IAP.
    // → On informe user du futur bonus, puis purchaseSubscription standard.
    const bonus = await promptCreditApplication({
      token, getValidToken, itemPriceEur: priceEur,
      itemLabel: lang === 'fr' ? `${label} (bonus)` : `${label} (bonus)`,
      lang,
    });
    const IAP = require('./IAPService');
    const result = await IAP.purchaseSubscription(tier, period, user);
    if (bonus > 0 && result?.success !== false) {
      // Consommer crédits côté backend + bonus apparaîtra dans les jours suivants
      try {
        const { useCredits } = require('./MytenueCredits');
        const authToken = getValidToken ? await getValidToken() : token;
        const txId = result?.transactionIdentifier
          || result?.customerInfo?.originalAppUserId
          || `${tier}_${period}_${Date.now()}`;
        await useCredits(authToken, {
          type: 'tier_upgrade',
          itemId: `${tier}_${period}`,
          itemName: label,
          amountUsed: bonus,
          meta: { iap_transaction_id: txId, gateway: Platform.OS === 'ios' ? 'apple_iap' : 'google_play', bonus_type: 'extra_days' },
        });
      } catch (e) { console.warn('[CreditAware] useCredits IAP err:', e?.message); }
    }
    return result;
  }
}

/**
 * Add-on one-shot
 * @param {object} ctx { addonId, user, getValidToken, lang }
 */
export async function purchaseAddonWithCredits({ addonId, user, getValidToken, lang = 'fr' }) {
  const priceEur = getAddonPriceEur(addonId);
  const label = ADDONS[addonId]?.labelFr || addonId;
  const token = user?.accessToken;

  if (isWeb) {
    const useCreditAmountEur = await promptCreditApplication({
      token, getValidToken, itemPriceEur: priceEur, itemLabel: label, lang,
    });
    const { startAddonCheckout } = require('./StripeService');
    return startAddonCheckout(addonId, { useCreditAmountEur });
  } else {
    const bonus = await promptCreditApplication({
      token, getValidToken, itemPriceEur: priceEur, itemLabel: `${label} (bonus)`, lang,
    });
    const IAP = require('./IAPService');
    const result = await IAP.purchaseAddon(addonId, user);
    if (bonus > 0 && result?.success !== false) {
      try {
        const { useCredits } = require('./MytenueCredits');
        const authToken = getValidToken ? await getValidToken() : token;
        const txId = result?.transactionIdentifier || `${addonId}_${Date.now()}`;
        await useCredits(authToken, {
          type: 'addon_purchase',
          itemId: addonId,
          itemName: label,
          amountUsed: bonus,
          meta: { iap_transaction_id: txId, gateway: Platform.OS === 'ios' ? 'apple_iap' : 'google_play', bonus_type: 'extra_units' },
        });
      } catch (e) { console.warn('[CreditAware] useCredits addon IAP err:', e?.message); }
    }
    return result;
  }
}

/**
 * Wardrobe subscription mensuelle (Plus / Max)
 */
export async function purchaseWardrobeSubscriptionWithCredits({ addonId, user, getValidToken, lang = 'fr' }) {
  const priceEur = getAddonPriceEur(addonId);
  const label = ADDONS[addonId]?.labelFr || addonId;
  const token = user?.accessToken;

  if (isWeb) {
    const useCreditAmountEur = await promptCreditApplication({
      token, getValidToken, itemPriceEur: priceEur, itemLabel: label, lang,
    });
    const { startWardrobeSubscriptionCheckout } = require('./StripeService');
    return startWardrobeSubscriptionCheckout(addonId, { useCreditAmountEur });
  } else {
    // Wardrobe sub IAP : pas implémenté sur mobile dans IAPService → fallback addon
    return purchaseAddonWithCredits({ addonId, user, getValidToken, lang });
  }
}
